import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Apple,
  Bot,
  Calculator,
  ClipboardPlus,
  History,
  LayoutDashboard,
  LogOut,
  Moon,
  Settings,
  Shield,
  Stethoscope,
  Sun,
  Timer,
} from "lucide-react";
import { useLang } from "../../context/LangContext";
import { useTheme } from "../../context/ThemeContext";
import { Button } from "../../components/ui/button";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import V2WelcomePopup from "./V2WelcomePopup";

export default function V2Layout({ children }) {
  const { lang, setLang } = useLang();
  const { theme, toggle } = useTheme();
  const { exitGuestMode, hasAdminAccess, isGuest, logout, logoutWarning, session } = useV2Session();
  const location = useLocation();
  const navigate = useNavigate();
  const copy = getV2Copy(lang);

  const navItems = [
    { to: "/dashboard", label: copy.dashboard, icon: LayoutDashboard },
    { to: "/foods", label: copy.foods, icon: Apple },
    { to: "/food-logging", label: copy.foodLogging, icon: ClipboardPlus },
    { to: "/history", label: copy.history, icon: History },
    { to: "/mixer", label: copy.mixer, icon: Calculator },
    { to: "/reminders", label: copy.reminders, icon: Timer },
    { to: "/assistant", label: copy.assistant, icon: Bot },
    { to: "/settings", label: copy.settings, icon: Settings },
    ...(hasAdminAccess ? [{ to: "/admin", label: copy.admin, icon: Shield }] : []),
  ];
  const showFloatingAssistant = location.pathname !== "/assistant";
  const assistantLabel = lang === "ar" ? "المساعد" : "Assistant";
  const floatingAssistantPosition =
    lang === "ar"
      ? "bottom-20 left-4 md:bottom-6 md:left-6"
      : "bottom-20 right-4 md:bottom-6 md:right-6";

  const leaveV2Session = async () => {
    if (isGuest) {
      const confirmed = window.confirm(
        "Leaving Guest Mode will sign out this anonymous account. Anonymous reminder records cannot be recovered after sign-out. Continue?"
      );
      if (!confirmed) return;

      const result = await exitGuestMode();
      if (result.ok) navigate("/");
      return;
    }

    const result = await logout();
    if (result.ok) navigate("/");
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground lg:flex">
      <aside className="shrink-0 border-b border-border bg-card lg:min-h-screen lg:w-72 lg:border-b-0 lg:border-e">
        <div className="flex items-center justify-between gap-3 p-5 lg:block">
          <Link to="/" className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Stethoscope className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-base font-extrabold">{copy.brand}</span>
            </span>
          </Link>
          <div className="flex gap-2 lg:mt-5">
            <Button variant="outline" size="sm" onClick={toggle} aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setLang(lang === "ar" ? "en" : "ar")}>
              {lang === "ar" ? "EN" : "AR"}
            </Button>
          </div>
        </div>

        <div className="px-4 pb-4">
          <div className="rounded-lg border border-border bg-background p-3 text-xs text-muted-foreground">
            <span className="block font-semibold text-foreground">
              {isGuest ? copy.guestMode : copy.registeredMode}
            </span>
            <span className="mt-1 block">
              {isGuest ? copy.guestStorageNotice : session?.user?.full_name || copy.profileSession}
            </span>
          </div>
        </div>

        <nav className="flex gap-2 overflow-x-auto px-4 pb-4 lg:block lg:space-y-1 lg:overflow-visible">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to || (item.to !== "/dashboard" && location.pathname.startsWith(item.to));

            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex min-w-max items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors lg:min-w-0 ${
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-border p-4">
          {logoutWarning && (
            <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
              {logoutWarning}
            </p>
          )}
          <Button variant="ghost" className="w-full justify-start gap-2 text-destructive" onClick={leaveV2Session}>
            <LogOut className="h-4 w-4" />
            {copy.logout}
          </Button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-x-hidden px-4 py-6 pb-28 md:px-8 md:py-8 md:pb-28">{children}</main>
      {showFloatingAssistant && (
        <Button
          asChild
          aria-label={assistantLabel}
          className={`fixed z-40 h-10 rounded-full px-3 shadow-lg ${floatingAssistantPosition}`}
        >
          <Link to="/assistant" className="gap-2">
            <Bot className="h-4 w-4" />
            <span className="text-sm font-semibold">{assistantLabel}</span>
          </Link>
        </Button>
      )}
      <V2WelcomePopup
        isGuest={isGuest}
        pathname={location.pathname}
        userId={session?.user?.id}
      />
    </div>
  );
}
